"use client";

import { ArrowLeft, Calendar, Tag, User } from "lucide-react";

import { Link } from "@/components/common/Link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

function SinglePostConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-4">
        <div className="h-10 w-3/4 rounded-md bg-muted animate-pulse mb-6" />
        <div className="h-80 w-full rounded-lg bg-muted animate-pulse mb-8" />
        <div className="space-y-3">
          <div className="h-4 w-full rounded bg-muted animate-pulse" />
          <div className="h-4 w-5/6 rounded bg-muted animate-pulse" />
          <div className="h-4 w-4/6 rounded bg-muted animate-pulse" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-4xl px-4 text-center">
        <p className="text-muted-foreground">Unable to load this article.</p>
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="mx-auto max-w-4xl px-4 text-center">
        <p className="text-muted-foreground">No article found.</p>
      </div>
    );
  }

  const post = posts[0];
  const formattedDate = post.date
    ? new Date(post.date).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  const authorInitials = post.author?.name
    ? post.author.name
        .split(" ")
        .map((n) => n[0])
        .slice(0, 2)
        .join("")
        .toUpperCase()
    : "A";

  return (
    <article className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <Link
          to="#"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Resources
        </Link>
      </div>

      <header className="mb-10">
        {post.categories && post.categories.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-5">
            {post.categories.map((cat, i) => (
              <Link
                key={cat.id}
                data-index={i}
                to={cat.link || "#"}
                className="inline-flex items-center gap-1.5 rounded-lg bg-accent px-3 py-1 text-xs font-medium text-accent-foreground hover:bg-accent/80 transition-colors"
              >
                <Tag className="h-3 w-3" />
                {cat.name}
              </Link>
            ))}
          </div>
        )}

        <h1 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-tight mb-6">
          {post.title}
        </h1>

        {post.excerpt && (
          <div
            className="text-lg text-muted-foreground leading-relaxed mb-8 font-default"
            dangerouslySetInnerHTML={{ __html: post.excerpt }}
          />
        )}

        <div className="flex flex-wrap items-center gap-x-6 gap-y-3 pb-6 border-b border-border">
          {post.author && (
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10">
                {post.author.avatar && post.author.avatar["96"] && (
                  <AvatarImage src={post.author.avatar["96"]} alt={post.author.name} />
                )}
                <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                  {authorInitials}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col">
                <span className="text-sm font-medium text-foreground">
                  {post.author.name}
                </span>
                <span className="text-xs text-muted-foreground">Author</span>
              </div>
            </div>
          )}

          {formattedDate && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar className="h-4 w-4" />
              <time dateTime={post.date}>{formattedDate}</time>
            </div>
          )}
        </div>
      </header>

      {post.featuredImage && (
        <div className="mb-10 overflow-hidden rounded-lg shadow-md">
          <img
            src={post.featuredImage}
            alt={post.title}
            loading="lazy"
            decoding="async"
            className="w-full h-auto object-cover"
          />
        </div>
      )}

      {post.content && (
        <div
          className="prose prose-lg max-w-none font-default text-foreground leading-relaxed [&>h1]:font-default [&>h1]:text-3xl [&>h1]:font-bold [&>h1]:mt-10 [&>h1]:mb-4 [&>h2]:font-default [&>h2]:text-2xl [&>h2]:font-bold [&>h2]:mt-8 [&>h2]:mb-4 [&>h3]:font-default [&>h3]:text-xl [&>h3]:font-semibold [&>h3]:mt-6 [&>h3]:mb-3 [&>p]:mb-5 [&>p]:text-base [&>p]:leading-7 [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-5 [&>ul]:space-y-2 [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:mb-5 [&>ol]:space-y-2 [&>blockquote]:border-l-4 [&>blockquote]:border-primary [&>blockquote]:pl-4 [&>blockquote]:italic [&>blockquote]:text-muted-foreground [&>blockquote]:my-6 [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2 hover:[&_a]:opacity-80 [&_img]:rounded-lg [&_img]:my-6 [&_code]:font-mono [&_code]:text-sm [&_code]:bg-muted [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      )}

      {post.tags && post.tags.length > 0 && (
        <div className="mt-10 pt-6 border-t border-border">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-foreground mr-2">Tags:</span>
            {post.tags.map((tag, i) => (
              <Link
                key={tag.id}
                data-index={i}
                to={tag.link || "#"}
                className="inline-flex items-center rounded-lg border border-border bg-background px-3 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
              >
                #{tag.name}
              </Link>
            ))}
          </div>
        </div>
      )}

      {post.author && (
        <Card className="mt-12 bg-card border border-border shadow-md">
          <CardContent className="flex flex-col sm:flex-row items-start gap-5 pt-6">
            <Avatar className="h-16 w-16 shrink-0">
              {post.author.avatar && post.author.avatar["96"] && (
                <AvatarImage src={post.author.avatar["96"]} alt={post.author.name} />
              )}
              <AvatarFallback className="bg-primary text-primary-foreground">
                {authorInitials}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <User className="h-4 w-4 text-muted-foreground" />
                <span className="text-xs uppercase tracking-wide text-muted-foreground font-medium">
                  About the Author
                </span>
              </div>
              <h3 className="font-default text-xl font-semibold text-foreground mb-2">
                {post.author.name}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Dedicated to providing advocacy, education, and compassionate support for neurodiverse families and individuals of all abilities.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <Separator className="my-12" />
    </article>
  );
}

function RelatedPostsConsumer() {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[...Array(3)].map((_, i) => (
          <div key={i} data-index={i} className="h-64 rounded-lg bg-muted animate-pulse" />
        ))}
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <p className="text-center text-muted-foreground">No related articles found.</p>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {posts.map((post, i) => {
        const formattedDate = post.date
          ? new Date(post.date).toLocaleDateString(undefined, {
              year: "numeric",
              month: "short",
              day: "numeric",
            })
          : "";
        return (
          <Card
            key={post.id}
            data-index={i}
            className="bg-card border border-border overflow-hidden shadow-md hover:shadow-lg transition-shadow"
          >
            {post.featuredImage && (
              <div className="aspect-[16/10] overflow-hidden">
                <img
                  src={post.featuredImage}
                  alt={post.title}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                />
              </div>
            )}
            <CardContent className="flex flex-col gap-3 px-6">
              {post.categories && post.categories.length > 0 && (
                <span className="inline-flex items-center self-start rounded-lg bg-accent px-2.5 py-0.5 text-xs font-medium text-accent-foreground">
                  {post.categories[0].name}
                </span>
              )}
              <h3 className="font-default text-lg font-semibold text-foreground leading-snug line-clamp-2">
                {post.title}
              </h3>
              {formattedDate && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Calendar className="h-3 w-3" />
                  <time dateTime={post.date}>{formattedDate}</time>
                </div>
              )}
              <Link to={post.link || "#"} className="mt-2">
                <Button variant="outline" size="sm" className="w-full border-primary text-primary hover:bg-primary/10">
                  Read Article
                </Button>
              </Link>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

export default function PostTemplate() {
  const currentWPQueryParams = (window as any).wvcClient.getCurrentWPQueryParams();
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section data-section-id="825"
      id="single-post-template-section"
      className="bg-background text-foreground py-16 sm:py-20 lg:py-24"
    >
      <PostsProvider wp_query={wp_query}>
        <SinglePostConsumer />
      </PostsProvider>

      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 mt-12">
        <div className="mb-10 text-center">
          <span className="inline-block text-xs uppercase tracking-widest text-primary font-semibold mb-3">
            Keep Reading
          </span>
          <h2 className="font-default text-3xl sm:text-4xl font-bold text-foreground mb-3">
            Related Articles
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Discover more resources, advocacy tips, and community stories from Hope Torchlighters.
          </p>
        </div>

        <PostsProvider wp_query={{ post_type: "post", per_page: 3, orderby: "date", order: "desc" }}>
          <RelatedPostsConsumer />
        </PostsProvider>

        <div className="mt-12 text-center">
          <Link to="#">
            <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
              View All Resources
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}